import React, { createContext, useContext, useMemo } from 'react';

import {
  TLatestPipelineExecutionsContextValue,
  TLatestPipelineExecutionsProviderProps,
} from '@/console/features/pipelines/hooks/use-latest-pipeline-executions/latest-pipeline-executions.types';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import { usePollingQuery } from '@/shared/hooks/use-polling-query/use-polling-query';
import { useReadLatestExecutionsForPipelinesQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  PipelineExecutionFragment,
  ReadLatestExecutionsForPipelinesQuery,
} from '@/shared/types/types.generated';

const PIPELINE_EXECUTION_POLLING_INTERVAL_MS = 30_000;

const LatestPipelineExecutionsContext =
  createContext<TLatestPipelineExecutionsContextValue>({
    isEnabled: false,
    isLoading: false,
    getExecution: () => undefined,
  });

// Fetches the newest execution for every pipeline in one batched query, replacing the
// two per-row execution fetches on the home recent-pipelines list. Polls at the batch
// level (stopping once nothing is running) so status chips stay live.
export const LatestPipelineExecutionsProvider: React.FC<
  TLatestPipelineExecutionsProviderProps
> = ({ pipelineConfigurationIds, children }) => {
  const { isJobRunning } = useJobStatus();

  const shouldStopPolling = (data: ReadLatestExecutionsForPipelinesQuery) =>
    data?.readLatestExecutionsForPipelines.every(
      (execution) => !isJobRunning(execution.status),
    ) ?? true;

  const { data, isLoading } = usePollingQuery({
    useQueryHook: useReadLatestExecutionsForPipelinesQuery,
    queryArg: { pipelineConfigurationIds },
    queryOptions: { skip: pipelineConfigurationIds.length === 0 },
    shouldStopPolling,
    pollingInterval: PIPELINE_EXECUTION_POLLING_INTERVAL_MS,
  });

  const value = useMemo<TLatestPipelineExecutionsContextValue>(() => {
    const executionByPipeline = new Map<string, PipelineExecutionFragment>(
      (data?.readLatestExecutionsForPipelines ?? []).map((execution) => [
        execution.pipelineConfigurationId,
        execution,
      ]),
    );
    return {
      isEnabled: true,
      isLoading,
      getExecution: (pipelineConfigurationId: string) =>
        executionByPipeline.get(pipelineConfigurationId),
    };
  }, [data, isLoading]);

  return (
    <LatestPipelineExecutionsContext.Provider value={value}>
      {children}
    </LatestPipelineExecutionsContext.Provider>
  );
};

export const useLatestPipelineExecutionsContext = () =>
  useContext(LatestPipelineExecutionsContext);
